/**
 * Sampling for the *billed* client error stream (TES-2191).
 *
 * WHY THIS EXISTS
 *
 * The org is on Sentry's Developer plan: 5,000 errors a month, about 167 a day.
 * Measured demand after the homepage count fan-out was fixed (#1063) is still
 * roughly 1,100 a day — 6-7x the cap — so the quota is spent in the first days
 * of each billing period and the project is blind for the rest of it. The
 * 2026-09-15 period burned its whole allowance in about 30 hours: 4,317 events
 * accepted on 09-15, 1,144 on 09-16, then zero through 09-21 while 1,000-2,700
 * a day were refused as `error_usage_exceeded`. A daily triage sweep against a
 * frozen stream reads as "no new errors", which is the opposite of the truth.
 *
 * Sending a sampled fraction keeps ingestion alive for all thirty days. Random
 * sampling preserves *relative* frequency, so the loudest defect is still the
 * loudest one in Sentry; it just arrives at 1-in-N and stops crowding out
 * everything that follows it in the month.
 *
 * WHY THIS IS NOT THE SDK'S OWN `sampleRate` OPTION
 *
 * `Sentry.init({ sampleRate })` and `beforeSend` run in an order that is not
 * stable across SDK releases. In @sentry/core 10.69 — the version this project
 * pins — `_processEvent` applies `sampleRate` *before* `beforeSend`; in 10.73 it
 * applies it *after*. `beforeSend` is where `recordClientErrorCensus` runs, and
 * the census is the only complete record of the client error stream we have
 * while the billed stream is sampled (see lib/client-error-census.ts). Under the
 * pinned version the built-in option would therefore sample the census away too,
 * and a patch bump would silently flip the behaviour back and forth.
 *
 * Sampling here — inside `beforeSend`, after the census has counted the event —
 * is independent of that ordering: the census always sees 100% of events and
 * only the envelope that would be billed is dropped.
 *
 * Server and edge runtimes are deliberately left unsampled. They are low-volume
 * and high-signal: a `SupabaseError` on `GET /` names a real production fault,
 * and there is no census behind them to fall back on.
 */

/**
 * Fraction of client error events forwarded to Sentry.
 *
 * 167 events/day of headroom against ~1,100/day of measured demand. Raise this
 * when demand drops (the census in the Vercel runtime log is the free way to
 * measure it) or if the plan's quota changes.
 */
export const CLIENT_ERROR_SAMPLE_RATE = 0.15

/**
 * Whether this event should be forwarded to Sentry rather than dropped.
 *
 * `random` is injectable so the decision can be tested without stubbing global
 * `Math.random`, which other suites in this project rely on.
 */
export function shouldSendSampledClientEvent(
  sampleRate: number = CLIENT_ERROR_SAMPLE_RATE,
  random: () => number = Math.random,
): boolean {
  // A rate at or above 1 disables sampling entirely; a non-positive or
  // non-finite rate is treated as "drop", so a bad value can never be read as
  // "send everything" and re-exhaust the quota.
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) return false
  if (sampleRate >= 1) return true
  return random() < sampleRate
}
